import { format, getYear, isAfter, isBefore, isValid, parse, subYears } from 'date-fns';

import React, { useEffect, useState } from 'react';
import { Popper, Input, ClickAwayListener } from '@mui/base';
import { styled } from '@mui/system';
import { enUS, zhCN, ko } from 'date-fns/locale';
import classNames from 'classnames';

import { DayPicker, DateRange } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './datepicker.css';
const RangeDatePickerRoot = styled('div', {
  name: 'MuiDatePicker',
  slot: 'Root',
})((props) => ({
  width: 'auto',
}));

interface RangeDatePickerProps {
  lang?: string;
  placeholder?: string;
  fromDate: string;
  toDate: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  enableMonthSelection?: boolean;
  disableFutureDays?: boolean;
  className?: string;
  joinText?: string;
  boxClassName?: string;
  inputClassName?: string;
  disabled?: boolean;
  disablePrevDays?: boolean;
}

const CalenderButton = styled('div')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: inherit;
  cursor: pointer;
  position: absolute;
  top: 0;
  height: 100%;
  right: 10px;
`;

const StyledInputElement = styled('input')`
  width: 100%;
`;

const StyledInputRoot = styled('div')`
  position: relative;
`;

const RangeDatePicker = (props: RangeDatePickerProps) => {
  const {
    lang,
    fromDate,
    onFromChange,
    toDate,
    onToChange,
    placeholder,
    enableMonthSelection,
    disableFutureDays,
    disablePrevDays,
    joinText = '-',
    boxClassName,
    inputClassName,
    disabled = false,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRange, setSelectedRange] = useState<DateRange>();
  const defaultDateFormat = 'y-MM-dd';
  const defaultMonth = fromDate ? new Date(fromDate) : new Date();
  const today = new Date();
  const options: any = {};
  options.disabled = disableFutureDays ? { after: today } : disablePrevDays ? { before: today } : {};

  const id = isOpen ? 'calender-popper' : undefined;

  useEffect(() => {
    if (fromDate || toDate) {
      setSelectedRange({
        from: fromDate ? new Date(fromDate) : undefined,
        to: toDate ? new Date(toDate) : undefined,
      });
    }
  }, []);

  useEffect(() => {
    if (!fromDate && !toDate) {
      setSelectedRange({ from: undefined, to: undefined });
    }
    if ((fromDate || toDate) && (!selectedRange?.from || !selectedRange?.to)) {
      setSelectedRange({
        from: fromDate ? new Date(fromDate) : undefined,
        to: toDate ? new Date(toDate) : undefined,
      });
    }
  }, [fromDate, toDate]);

  // When the input field loses focus, we need to parse
  // the input to set the date. While doing this, we also do some
  // assumptions for the user and fix mistakes.
  const onInputBlur = () => {
    // If the input is empty, we should just go ahead and
    // clear the current selection.
  };

  const [fromValue, setFromValue] = useState<string>('');
  const [toValue, setToValue] = useState<string>('');

  const handleFromChange = (e) => {
    setFromValue(e.target.value);
    const date = parse(e.target.value, defaultDateFormat, new Date());
    if (!isValid(date)) {
      return setSelectedRange({ from: undefined, to: undefined });
    }
    if (selectedRange?.to && isAfter(date, selectedRange.to)) {
      setSelectedRange({ from: selectedRange.to, to: date });
    } else {
      setSelectedRange({ from: date, to: selectedRange?.to });
    }
  };

  const handleToChange = (e) => {
    setToValue(e.target.value);
    const date = parse(e.target.value, defaultDateFormat, new Date());

    if (!isValid(date)) {
      return setSelectedRange({ from: selectedRange?.from, to: undefined });
    }
    if (selectedRange?.from && isBefore(date, selectedRange.from)) {
      setSelectedRange({ from: date, to: selectedRange.from });
    } else {
      setSelectedRange({ from: selectedRange?.from, to: date });
    }
  };

  const updateFromDateAndToDate = (range) => {
    if (range?.from) {
      onFromChange(format(range.from, defaultDateFormat));
    } else {
      onFromChange('');
    }

    if (range?.to) {
      onToChange(format(range.to, defaultDateFormat));
    } else {
      onToChange('');
    }
  };

  const handleClickAway = () => {
    // user triggers unfocus
    updateFromDateAndToDate(selectedRange);
    setIsOpen(false);
  };

  const handleRangeSelect = (range) => {
    setSelectedRange(range);
    updateFromDateAndToDate(range);

    if (range?.from && range?.to) {
      setIsOpen(false);
    }
  };

  const getLang = (lang) => {
    switch (lang) {
      case 'en':
        return enUS;
      case 'zh':
        return zhCN;
      case 'ko':
        return ko;
      default:
        return enUS;
    }
  };

  let caption = {};
  if (enableMonthSelection) {
    caption = {
      captionLayout: 'dropdown-buttons',
      fromYear: subYears(new Date(), 15).getFullYear(),
      toYear: getYear(new Date()),
    };
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <RangeDatePickerRoot>
        <div ref={(elm) => setAnchorEl(elm)} className={classNames('flex items-center justify-center', boxClassName)}>
          <Input
            disabled={disabled}
            endAdornment={
              <CalenderButton
                onClick={() => {
                  if (!disabled) {
                    setIsOpen(!isOpen);
                  }
                }}
              >
                <span className="icon-24-calendar-mono"></span>
              </CalenderButton>
            }
            slotProps={{ input: { className: `c-input ${props.className ?? ''}` } }}
            slots={{
              root: StyledInputRoot,
              input: StyledInputElement,
            }}
            onBlur={() => onInputBlur()}
            onChange={handleFromChange}
            placeholder={placeholder || ''}
            value={fromDate}
            className={classNames(inputClassName)}
          />
          <span className="whitespace-nowrap mx-2">{joinText}</span>
          <Input
            disabled={disabled}
            endAdornment={
              <CalenderButton
                onClick={() => {
                  if (!disabled) {
                    setIsOpen(!isOpen);
                  }
                }}
              >
                <span className="icon-24-calendar-mono"></span>
              </CalenderButton>
            }
            slotProps={{ input: { className: `c-input ${props.className ?? ''}` } }}
            slots={{
              root: StyledInputRoot,
              input: StyledInputElement,
            }}
            onBlur={() => onInputBlur()}
            onChange={handleToChange}
            placeholder={placeholder || ''}
            value={toDate}
            className={classNames(inputClassName)}
          />
        </div>
        <Popper id={id} open={isOpen} anchorEl={anchorEl} placement="bottom">
          <div tabIndex={-1} role="dialog" aria-label="DayPicker range calendar" className="dialog-sheet">
            <DayPicker
              locale={getLang(lang)}
              {...caption}
              mode="range"
              defaultMonth={defaultMonth}
              numberOfMonths={2}
              selected={selectedRange}
              onSelect={handleRangeSelect}
              {...options}
            />
          </div>
        </Popper>
      </RangeDatePickerRoot>
    </ClickAwayListener>
  );
};

export default RangeDatePicker;
